/**
 * Where a composer menu goes when it pops up (item BT).
 *
 * The mode and model menus open *upward* from their chip. In the conversation
 * view the composer lives in `.composer-wrap`, which scrolls (`overflow-y:
 * auto`, so a short window can still reach the send button) — and an element
 * that scrolls also clips, absolutely positioned descendants included. The
 * menu was drawn, above the card, into the wrapper's 0.75rem of top padding,
 * and cut off there: "we lost Review and Auto once the conversation started".
 * The new-conversation hero has no such wrapper, which is why it worked there.
 *
 * `position: fixed` escapes every overflow box, so the menu is placed in
 * viewport coordinates instead: its bottom edge a small gap above the chip's
 * top, its right edge on the chip's right (the row is right-aligned, and a
 * wide menu hanging left keeps it on the card). Pure, so it can be tested.
 */

export interface AnchorRect {
  top: number;
  right: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface AnchoredStyle {
  position: "fixed";
  bottom: number;
  right: number;
  left: "auto";
}

/** The gap between the chip's top and the menu's bottom, matching the CSS `calc(100% + 0.35rem)` at 16px. */
export const MENU_GAP_PX = 6;

export function anchorAbove(chip: AnchorRect, viewport: Viewport, gap = MENU_GAP_PX): AnchoredStyle {
  return {
    position: "fixed",
    bottom: Math.max(0, viewport.height - chip.top + gap),
    right: Math.max(0, viewport.width - chip.right),
    left: "auto",
  };
}

/**
 * Where a **mention chip's** popover goes (#58 item 3).
 *
 * `anchorAbove` will not do here, and the difference is not style. A composer
 * control sits at a known corner of a right-aligned row, so "above, right
 * edges flush" is always right. A mention chip is a word inside the user's
 * sentence: it can be on any line, at either margin, near the top of the
 * composer or the bottom. So the popover opens **below and left-aligned** with
 * the chip — the reading direction, and where the reference puts it (`align:
 * "start"`) — and flips above only when it would otherwise fall off the bottom.
 *
 * `position: fixed` for the same reason as above: `.composer-wrap` scrolls, and
 * an element that scrolls also clips.
 *
 * `maxHeight` comes back with the position because the two are one decision. A
 * panel listing every parameter of a wall type is taller than the space under a
 * chip on the last line, and the honest answer is to let it scroll in the room
 * it has rather than run off the screen.
 */
export interface ChipRect {
  top: number;
  bottom: number;
  left: number;
}

export interface AnchoredPanel {
  position: "fixed";
  left: number;
  /** Set when the panel opens downward; `bottom` is set instead when it flips. */
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/** Never squeeze the panel below this before flipping — a 2-row list is not a list. */
export const MIN_PANEL_PX = 180;

/** Keep the panel off the very edge of the window. */
const EDGE_PAD_PX = 8;

export function anchorBelow(
  chip: ChipRect,
  viewport: Viewport,
  panelWidth: number,
  gap = MENU_GAP_PX,
): AnchoredPanel {
  const below = viewport.height - chip.bottom - gap - EDGE_PAD_PX;
  const above = chip.top - gap - EDGE_PAD_PX;

  // Left-aligned with the chip, pulled back only far enough to stay on screen —
  // a chip near the right margin would otherwise open a panel half off it.
  const left = Math.max(EDGE_PAD_PX, Math.min(chip.left, viewport.width - panelWidth - EDGE_PAD_PX));

  // Prefer below. Flip only when below is genuinely too small *and* above is
  // roomier, so a chip in a short window does not flip back and forth.
  if (below >= MIN_PANEL_PX || below >= above) {
    return { position: "fixed", top: chip.bottom + gap, left, maxHeight: Math.max(0, below) };
  }
  return { position: "fixed", bottom: viewport.height - chip.top + gap, left, maxHeight: Math.max(0, above) };
}
